import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts';
import { deployAPI, costAPI, monitorAPI, cloudAPI, integrationAPI } from '../services/api';
import { useAuth } from '../services/auth';
import { useOrgRole } from '../hooks/useOrgRole';
import ClockWeather from '../components/common/ClockWeather';
import QuickStatusBar from '../components/common/QuickStatusBar';

const COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };
const PIE_COLORS = ['#ff9900','#4285f4','#0078d4','#0080ff'];
const INTEGRATION_IDS = ['jenkins','github_actions','gitlab_ci','circleci','argocd','kubernetes','docker','helm','terraform','ansible','pulumi','prometheus','grafana','datadog','newrelic','elastic','vault','snyk','sonarqube','custom_cloud','onprem','openstack','slack','pagerduty','webhook'];

function Gauge({ value, max, label, color, sub }) {
  const pct = max > 0 ? Math.min(100, (value/max)*100) : 0;
  const data = [{ name: label, value: pct, fill: color }];
  return (
    <div className="card" style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:20 }}>
      <div style={{ width:'100%', height:120, position:'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" cornerRadius={10} background={{ fill:'var(--border)' }} clockWise />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center' }}>
          <div style={{ fontSize:26, fontWeight:700, color:'var(--text-primary)' }}>{value}</div>
        </div>
      </div>
      <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:6 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:'var(--text-secondary)', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { can } = useOrgRole();

  const showOps     = can('opsView');
  const showMonitor = can('monitorView');
  const showCosts   = can('costView');

  const [stats,   setStats]   = useState({ total:0, running:0, stopped:0, failed:0, byProvider:[] });
  const [costs,   setCosts]   = useState(null);
  const [monitor, setMonitor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clouds, setClouds] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const [range, setRange] = useState(7);
  const [serverSearch, setServerSearch] = useState('');

  const fetchAll = async (isManual = false, days = range) => {
    if (isManual) setRefreshing(true);
    try {
      const jobs = [];
      const keys = [];
      if (showOps) {
        jobs.push(deployAPI.stats());   keys.push('stats');
        jobs.push(cloudAPI.list());     keys.push('clouds');
        jobs.push(integrationAPI.list()); keys.push('integrations');
      }
      if (showMonitor) {
        jobs.push(monitorAPI.overview()); keys.push('monitor');
      }
      if (showCosts) {
        jobs.push(costAPI.list({ days })); keys.push('costs');
      }

      const results = await Promise.allSettled(jobs);

      results.forEach((r, i) => {
        const key = keys[i];
        if (r.status !== 'fulfilled') {
          console.error(`Dashboard: failed to load ${key}`, r.reason);
          return;
        }
        const data = r.value.data.data;
        if (key === 'stats') setStats(data);
        if (key === 'clouds') setClouds(data || []);
        if (key === 'integrations') setIntegrations(data || []);
        if (key === 'monitor') setMonitor(data);
        if (key === 'costs') setCosts(data);
      });

      setLastSynced(new Date());
      setCountdown(60);
    } catch(e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    fetchAll(false, range);
    const interval = setInterval(() => fetchAll(false, range), 60000);
    const cd = setInterval(() => setCountdown(c => c <= 1 ? 60 : c - 1), 1000);
    return () => { clearInterval(interval); clearInterval(cd); };
  }, [range]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const pieData = (costs?.byProvider || []).map((p, i) => ({
    name: p.provider.toUpperCase(), value: p.total, color: PIE_COLORS[i],
  }));

  const integrationCount = integrations.filter(i => INTEGRATION_IDS.includes(i.toolId)).length;
  const criticalServers = monitor.filter(d => (d.health?.cpu > 80) || (d.health?.memory > 80) || d.status !== 'running');
  const connectedClouds = clouds.filter(c => c.isConnected);
  const filteredMonitor = monitor.filter(d => d.name.toLowerCase().includes(serverSearch.toLowerCase()));

  const gaugeCount = (showOps ? 3 : 0) + (showMonitor ? 1 : 0);

  return (
    <div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div className="page-header" style={{ flexWrap:'wrap', gap:16 }}>
        <div>
          <h1>Welcome back, {user?.name?.split(' ')[0]} 👋</h1>
          <p>Here's your multi-cloud overview for today</p>
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:20, padding:'12px 20px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, marginBottom:20 }}>
        <ClockWeather compact />
        <div style={{ width:1, height:24, background:'var(--border)' }} />
        {showMonitor && <QuickStatusBar criticalCount={criticalServers.length} />}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:16 }}>
          {lastSynced && <span style={{ fontSize:12, color:'var(--text-secondary)' }}>Synced {lastSynced.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} · next in {countdown}s</span>}
          <button
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            style={{ background:'transparent', border:'1px solid var(--border)', color:'var(--text-secondary)', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:6 }}
          >
            <span style={{ display:'inline-block', animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>⟳</span>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {gaugeCount > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${gaugeCount}, 1fr)`, gap:20, marginBottom:24 }}>
          {showOps && <Gauge value={stats?.total||0} max={Math.max(stats?.total||0,1)} label="Total Servers" color="var(--accent)" sub={`${stats?.running||0} running`} />}
          {showOps && <Gauge value={connectedClouds.length} max={Math.max(clouds.length,1)} label="Cloud Providers" color="var(--purple)" sub={connectedClouds.map(c=>c.provider.toUpperCase().slice(0,3)).join(' · ') || 'None'} />}
          {showOps && <Gauge value={integrationCount} max={Math.max(INTEGRATION_IDS.length,1)} label="Integrations Connected" color="var(--green)" sub={`of ${INTEGRATION_IDS.length} tools`} />}
          {showMonitor && <Gauge value={criticalServers.length} max={Math.max(monitor.length,1)} label="Critical Servers" color="var(--red)" sub={criticalServers.length>0 ? 'Needs attention' : 'All healthy'} />}
        </div>
      )}

      {showCosts && (
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:20, marginBottom:24 }}>
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ fontSize:16 }}>Cost Trend</h2>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                {[{l:'Day',d:1},{l:'Week',d:7},{l:'Month',d:30}].map(r => (
                  <button
                    key={r.d}
                    onClick={() => setRange(r.d)}
                    style={{
                      background: range===r.d ? 'var(--accent)' : 'transparent',
                      color: range===r.d ? '#fff' : 'var(--text-secondary)',
                      border:'1px solid var(--border)', borderRadius:6, padding:'4px 10px',
                      fontSize:12, cursor:'pointer'
                    }}
                  >{r.l}</button>
                ))}
                <span className="badge badge-blue">USD</span>
              </div>
            </div>
            {(costs?.daily?.length === 0 || !costs?.daily) ? (
              <div className="empty" style={{ padding:'40px 0' }}>
                <div className="empty-icon">📊</div>
                <h3>No cost data yet</h3>
                <p>Create a deployment to start tracking costs</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={costs.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill:'var(--text-secondary)', fontSize:11 }} />
                  <YAxis tick={{ fill:'var(--text-secondary)', fontSize:11 }} tickFormatter={v=>`$${v}`} />
                  <Tooltip contentStyle={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)' }} />
                  {Object.entries(COLORS).map(([k,v]) => (
                    <Area key={k} type="monotone" dataKey={k} stroke={v} strokeWidth={2} fill="transparent" />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <h2 style={{ fontSize:16, marginBottom:20 }}>Cost by Provider</h2>
            {pieData.length === 0 ? (
              <div className="empty" style={{ padding:'30px 0' }}>
                <div className="empty-icon">🍩</div>
                <h3>No spend data</h3>
                <p>Deploy something first</p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={v=>[`$${v}`,'']} contentStyle={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:8 }}>
                  {pieData.map((p,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:p.color }} />
                      <span style={{ color:'var(--text-secondary)' }}>{p.name}</span>
                      <span style={{ fontWeight:600 }}>${p.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showMonitor && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <h2 style={{ fontSize:16 }}>Live Health Monitor</h2>
            <input type="text" placeholder="Search server..." value={serverSearch} onChange={e => setServerSearch(e.target.value)} style={{ background:"var(--bg-secondary)", border:"1px solid var(--border)", color:"var(--text-primary)", borderRadius:8, padding:"6px 12px", fontSize:12, width:200 }} />
            <Link to="/deployments" className="btn btn-secondary btn-sm">View All</Link>
          </div>
          {monitor.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🚀</div>
              <h3>No active deployments</h3>
              <p>Click "+ New Deployment" to deploy your first workload</p>
              <Link to="/deployments" className="btn btn-primary" style={{ marginTop:16 }}>Create Deployment →</Link>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16 }}>
              {filteredMonitor.map(d => {
                const isCritical = (d.health?.cpu > 80) || (d.health?.memory > 80) || d.status !== 'running';
                return (
                  <div key={d.id} style={{ border:`1px solid ${isCritical ? 'var(--red)' : 'var(--border)'}`, borderRadius:10, padding:14, background:'var(--bg-secondary)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <strong style={{ fontSize:13 }}>{d.name}</strong>
                      <span className="badge badge-green">● {d.status}</span>
                    </div>
                    <div style={{ fontSize:11, color:COLORS[d.provider], fontWeight:600, textTransform:'uppercase', marginBottom:10 }}>{d.provider} · {d.region}</div>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-secondary)', marginBottom:3 }}><span>CPU</span><span>{d.health?.cpu}%</span></div>
                      <div style={{ width:'100%', height:6, background:'var(--border)', borderRadius:3 }}>
                        <div style={{ width:`${d.health?.cpu}%`, height:'100%', background: d.health?.cpu>80?'var(--red)':'var(--accent)', borderRadius:3 }} />
                      </div>
                    </div>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-secondary)', marginBottom:3 }}><span>Memory</span><span>{d.health?.memory}%</span></div>
                      <div style={{ width:'100%', height:6, background:'var(--border)', borderRadius:3 }}>
                        <div style={{ width:`${d.health?.memory}%`, height:'100%', background: d.health?.memory>80?'var(--red)':'var(--green)', borderRadius:3 }} />
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-secondary)' }}>
                      <span>Uptime: {d.uptime}m</span>
                      <span style={{ color:'var(--yellow)' }}>${d.cost?.hourlyRate?.toFixed(4)}/hr</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!showOps && !showMonitor && !showCosts && (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">👋</div>
            <h3>Welcome to CloudUnify</h3>
            <p>Your role doesn't have access to any dashboard widgets yet. Contact your organization owner if you think this is a mistake.</p>
          </div>
        </div>
      )}
    </div>
  );
}
